import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, MessageCircle, UserMinus, UserPlus, Search, Loader2, Check } from 'lucide-react';
import Layout from '../components/Layout';
import { Card, Button, IconBadge } from '../components/ui';
import { communityApi, CommunityUser } from '../api';
import { formatHandicapIndex } from '../utils/handicapIndex';

const Circle: React.FC = () => {
  const [circle, setCircle] = useState<CommunityUser[]>([]);
  const [suggestions, setSuggestions] = useState<CommunityUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CommunityUser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Debounced live search on name or email.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setHasSearched(false);
      return;
    }
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const data = await communityApi.searchUsers(q);
        setResults(Array.isArray(data) ? data : []);
        setHasSearched(true);
      } catch {
        // A stale search failing shouldn't nuke the page; keep prior results.
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const load = async () => {
    setError('');
    setIsLoading(true);
    try {
      const [mine, sugg] = await Promise.all([communityApi.getMyCircle(), communityApi.getSuggestions(12)]);
      setCircle(Array.isArray(mine) ? mine : []);
      setSuggestions(Array.isArray(sugg) ? sugg : []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load circle');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const markResult = (userId: number, inCircle: boolean) =>
    setResults((prev) => prev.map((x) => (x.id === userId ? { ...x, is_in_circle: inCircle } : x)));

  const handleFollow = async (u: CommunityUser) => {
    setError('');
    try {
      await communityApi.followUser(u.id);
      setSuggestions((prev) => prev.filter((x) => x.id !== u.id));
      setCircle((prev) => [u, ...prev]);
      markResult(u.id, true);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to add to circle');
    }
  };

  const handleUnfollow = async (u: CommunityUser) => {
    setError('');
    try {
      await communityApi.unfollowUser(u.id);
      setCircle((prev) => prev.filter((x) => x.id !== u.id));
      setSuggestions((prev) => [u, ...prev]);
      markResult(u.id, false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to remove from circle');
    }
  };

  return (
    <Layout>
      <div className="pb-20 space-y-6">
        <Card>
          <div className="flex items-center space-x-3">
            <IconBadge tone="gold"><Users /></IconBadge>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Find Golfers</h1>
              <p className="text-sm text-gray-500">Find the usual suspects. Golf is cheaper without them, but far less funny.</p>
            </div>
          </div>
        </Card>

        {/* Search */}
        <Card>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search golfers by name or email…"
              className="w-full pl-10 pr-10 py-3 bg-gray-100 border-0 rounded-xl text-gray-800 placeholder-gray-400 focus:ring-2 focus:ring-emerald-500"
            />
            {isSearching && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 animate-spin" />
            )}
          </div>

          {query.trim().length >= 2 && (
            <div className="mt-4">
              {results.length === 0 ? (
                hasSearched && !isSearching ? (
                  <p className="text-sm text-gray-500 text-center py-2">
                    No golfers found for “{query.trim()}”. They may not have joined yet — send them your app link!
                  </p>
                ) : null
              ) : (
                <div className="space-y-3">
                  {results.map((u) => (
                    <div key={u.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100">
                      <Link to={`/golfers/${u.id}`} className="flex items-center space-x-3 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center overflow-hidden">
                          {u.avatar_url ? (
                            <img src={u.avatar_url} alt={u.full_name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-white font-semibold">{u.full_name?.charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-800 truncate">{u.full_name}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {u.home_club ? u.home_club : 'Golf buddy'}
                            {typeof u.handicap === 'number' ? ` • Handicap Index ${formatHandicapIndex(u.handicap)}` : ''}
                          </p>
                        </div>
                      </Link>

                      {u.is_in_circle ? (
                        <span className="inline-flex items-center space-x-1 text-emerald-700 bg-emerald-100 px-3 py-2 rounded-xl text-sm font-semibold">
                          <Check className="h-4 w-4" />
                          <span>In circle</span>
                        </span>
                      ) : (
                        <Button
                          size="sm"
                          leftIcon={<UserPlus className="h-4 w-4" />}
                          onClick={() => handleFollow(u)}
                        >
                          Add
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>

        {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">{error}</div>}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
          </div>
        ) : (
          <>
            <Card>
              <h2 className="text-lg font-bold text-gray-800 mb-4">In your circle</h2>
              {circle.length === 0 ? (
                <p className="text-sm text-gray-500">No one in your circle yet. Add a few golfers below.</p>
              ) : (
                <div className="space-y-3">
                  {circle.map((u) => (
                    <div key={u.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100">
                      <Link to={`/golfers/${u.id}`} className="flex items-center space-x-3 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center overflow-hidden">
                          {u.avatar_url ? (
                            <img src={u.avatar_url} alt={u.full_name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-white font-semibold">{u.full_name?.charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-800 truncate">{u.full_name}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {u.home_club ? u.home_club : 'Golf buddy'}
                            {typeof u.handicap === 'number' ? ` • Handicap Index ${formatHandicapIndex(u.handicap)}` : ''}
                          </p>
                        </div>
                      </Link>

                      <div className="flex items-center space-x-2 ml-3">
                        <Link
                          to={`/messages/new/${u.id}`}
                          className="inline-flex items-center justify-center p-2 bg-white rounded-xl border border-gray-200 hover:bg-gray-100 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400/70"
                          title="Message"
                        >
                          <MessageCircle className="h-5 w-5 text-gray-700" />
                        </Link>
                        <button
                          type="button"
                          onClick={() => handleUnfollow(u)}
                          className="inline-flex items-center justify-center p-2 bg-white rounded-xl border border-gray-200 hover:bg-gray-100 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400/70"
                          title="Remove"
                        >
                          <UserMinus className="h-5 w-5 text-gray-700" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card>
              <h2 className="text-lg font-bold text-gray-800 mb-4">Suggested golfers</h2>
              {suggestions.length === 0 ? (
                <p className="text-sm text-gray-500">No suggestions right now.</p>
              ) : (
                <div className="space-y-3">
                  {suggestions.map((u) => (
                    <div key={u.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100">
                      <Link to={`/golfers/${u.id}`} className="flex items-center space-x-3 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center overflow-hidden">
                          {u.avatar_url ? (
                            <img src={u.avatar_url} alt={u.full_name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-white font-semibold">{u.full_name?.charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-800 truncate">{u.full_name}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {u.home_club ? u.home_club : 'Golf buddy'}
                            {typeof u.handicap === 'number' ? ` • Handicap Index ${formatHandicapIndex(u.handicap)}` : ''}
                          </p>
                        </div>
                      </Link>

                      <Button
                        size="sm"
                        leftIcon={<UserPlus className="h-4 w-4" />}
                        onClick={() => handleFollow(u)}
                      >
                        Add
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

export default Circle;
